// Pure helpers for the LiveScribe mic pipeline (context/RecordingContext.jsx).
// No React or browser globals here, so they can be unit-tested in plain node.

// Pause/resume control frame for the live-scribe socket. The server's mic
// deficit alarm (live_scribe.py) compares audio-seconds received against
// wall-clock seconds; the client sends no audio while paused, so without these
// frames a pause -- and the Stop button, which pauses and then opens the
// Save/Discard dialog with the socket still open -- reads as a microphone
// dropping audio (142 of 495 "Microphone is dropping audio" warnings,
// 08-28..09-28).
// Sent on every pause/resume, and again ({type:"pause"}) when a socket
// (re)connects while paused, because a reconnect starts a fresh server session.
// Servers without pause support ignore unknown control types (their receive
// loop acts only on heartbeat / miclabel / stop / close), so this is always
// safe to send.
export function pauseControlFrame(paused) {
  return JSON.stringify({ type: paused ? "pause" : "resume" });
}

// "&miclabel=<label>" for the live-scribe WS URL, or "" while the mic is not
// known yet. The server logs it at connect (mic forensics); the query param
// survives reconnects, where the post-open control message was never re-sent.
// Built inside the reconnect timer, so it must never throw (encodeURIComponent
// does on a lone surrogate) or bloat the URL: forensics can't cost a reconnect.
const MIC_LABEL_MAX = 200;
export function micLabelQueryParam(label) {
  if (!label) return "";
  try {
    return `&miclabel=${encodeURIComponent(String(label).slice(0, MIC_LABEL_MAX))}`;
  } catch (_) {
    return "";
  }
}

// Browser-side delivery-rate meter behind the 'frame-drop deficit NN%' report:
// counts samples the worklet delivered against the wall clock over windows of
// at least windowMs. add() returns the ratio (1.0 = real time) when a window
// completes and starts the next one; otherwise null. reset() restarts the
// window -- call it while paused, so a window never spans a pause (nothing is
// captured then, which would read as dropped frames).
export function createRateWindow(now, windowMs = 20000) {
  let start = now;
  let samples = 0;
  return {
    reset(t) {
      start = t;
      samples = 0;
    },
    add(count, sampleRate, t) {
      samples += count;
      const winMs = t - start;
      if (winMs < windowMs) return null;
      const r = (samples / sampleRate) / (winMs / 1000);
      start = t;
      samples = 0;
      return r;
    },
  };
}
